'use client';

// Payout Calculator · "Calculadora (topo)" (Figma 12071:59983 desktop 792×664,
// 12471:9841 mobile 366×1500). A working client-side simulator: the default
// state ($50K · $2,500 · $2,000 · $5,000 · 25% · $2,000) reproduces the Figma
// numbers. The maths follow the rule copy on this page:
//   payout 1      = min(50% of profit, Payout Cap)        (Payout Cap rule)
//   best day      = consistency limit × profit            (Consistency Rule)
//   stays         = profit − payout 1 (= buffer)          (Buffer rule)
//   MDD line      = trailing, locks at the starting balance once profit ≥ max
//                   drawdown; a payout moves it to the starting balance
//   new profit    = at least half of payout 2 must be new profit, and the 50%
//                   rule still applies                    (New Profit Requirement)
import { useId, useState } from 'react';
import { cn } from '@/lib/utils';
import { FigureFrame } from '../primitives';

const ASSETS = '/general-rules-v3/payout-calculator';

const ACCOUNT_SIZES = [
  { label: '$50K', value: 50_000 },
  { label: '$100K', value: 100_000 },
  { label: '$200K', value: 200_000 },
] as const;

const LIMIT_MIN = 15;
const LIMIT_MAX = 40;
const DEFAULTS = { account: 50_000, maxDrawdown: 2500, cap: 2000, profit: 5000, limit: 25, request: 2000 };

/** Rule each label links to ("Click any rule name to read it"). */
const RULE_LINKS = {
  maxDrawdown: 'max-drawdown',
  cap: 'payout-cap',
  profit: 'maximum-payout-request',
  limit: 'consistency-rule',
  request: 'new-profit-requirement',
} as const;

const money = (n: number) => `$${Math.max(0, Math.round(n)).toLocaleString('en-US')}`;
const digits = (s: string) => s.replace(/[^\d]/g, '').slice(0, 9);

const LABEL = 'mb-0 whitespace-nowrap text-[2rem] font-medium leading-[3rem]';

function RuleLink({ slug, children }: { slug: string; children: React.ReactNode }) {
  return (
    <a
      href={`?rule=${slug}`}
      className={cn(LABEL, 'text-[#228e71] underline [text-underline-position:from-font]')}
    >
      {children}
    </a>
  );
}

function MoneyInput({
  id,
  label,
  slug,
  value,
  onChange,
  dataUi,
}: {
  id: string;
  label: string;
  slug: string;
  value: string;
  onChange: (next: string) => void;
  dataUi: string;
}) {
  return (
    <div data-ui={dataUi} className="flex w-full flex-col gap-[1rem]">
      <label htmlFor={id} className="w-fit">
        <RuleLink slug={slug}>{label}</RuleLink>
      </label>
      <div className="flex h-[5.5rem] w-full items-center gap-[2rem] rounded-[0.25rem] border border-black/10 bg-white px-[1.5rem] lg:h-[5rem] lg:gap-[1rem]">
        <span className="text-[2rem] font-normal leading-[3rem] text-black/40">$</span>
        <input
          id={id}
          type="text"
          inputMode="numeric"
          value={value}
          onChange={(e) => onChange(digits(e.target.value))}
          className="h-full min-w-0 flex-1 bg-transparent p-0 font-geist text-[2rem] font-medium leading-[3rem] text-black outline-none"
        />
      </div>
    </div>
  );
}

function Answer({
  question,
  note,
  value,
  tone = 'black',
  dataUi,
}: {
  question: React.ReactNode;
  note: React.ReactNode;
  value: string;
  tone?: 'black' | 'green';
  dataUi: string;
}) {
  return (
    <div
      data-ui={dataUi}
      className="flex w-full flex-col gap-[1rem] rounded-[0.25rem] border border-black/10 p-[2rem] lg:flex-row lg:items-center lg:gap-[2rem] lg:px-[2rem] lg:py-[1.75rem]"
    >
      <div className="flex min-w-0 flex-col gap-[0.25rem] text-[2rem] leading-[3rem] lg:flex-1">
        <p className="mb-0 font-medium text-black">{question}</p>
        <p className="mb-0 font-normal text-black/40">{note}</p>
      </div>
      <p
        className={cn(
          'mb-0 shrink-0 whitespace-nowrap text-[3rem] font-medium leading-[4rem]',
          tone === 'green' ? 'text-[#228e71]' : 'text-black',
        )}
      >
        {value}
      </p>
    </div>
  );
}

export default function PayoutCalculator() {
  const uid = useId();
  const [account, setAccount] = useState<number>(DEFAULTS.account);
  const [maxDrawdownRaw, setMaxDrawdownRaw] = useState(String(DEFAULTS.maxDrawdown));
  const [capRaw, setCapRaw] = useState(String(DEFAULTS.cap));
  const [profitRaw, setProfitRaw] = useState(String(DEFAULTS.profit));
  const [limit, setLimit] = useState(DEFAULTS.limit);
  const [requestRaw, setRequestRaw] = useState(String(DEFAULTS.request));

  const maxDrawdown = Number(maxDrawdownRaw) || 0;
  const cap = Number(capRaw) || 0;
  const profit = Number(profitRaw) || 0;
  const request = Number(requestRaw) || 0;

  // Payout 1: the lower of 50% of the profit and the Payout Cap.
  const halfProfit = profit / 2;
  const payout1 = Math.min(halfProfit, cap);
  const cappedByCap = cap <= halfProfit;

  // Best day allowed by the consistency limit.
  const bestDay = profit * (limit / 100);

  // Buffer after payout 1 = what stays above the starting balance.
  const stays = Math.max(0, profit - payout1);

  // Max Drawdown line: trails the balance until profit ≥ max drawdown, then locks
  // at the starting balance. A payout moves it to the starting balance anyway.
  const locked = profit >= maxDrawdown;
  const lineBefore = locked ? account : account + profit - maxDrawdown;
  const lineAfter = account;

  // Payout 2: at least half must be new profit; the 50% rule still applies.
  const request2 = Math.min(request, cap);
  const newProfit = Math.max(0, request2 / 2, 2 * request2 - stays);

  const fill = ((limit - LIMIT_MIN) / (LIMIT_MAX - LIMIT_MIN)) * 100;

  return (
    <FigureFrame
      data-ui="figure.payoutCalculator"
      padding="p-[2rem] lg:p-[3rem]"
      className="gap-[3rem] font-geist lg:flex-row lg:items-start"
    >
      {/* Entradas */}
      <div
        data-ui="figure.payoutCalculator.inputs"
        className="flex w-full flex-col gap-[2rem] rounded-[0.25rem] bg-[#f2f4f3] p-[2.5rem] lg:h-[74rem] lg:w-[35rem] lg:shrink-0 lg:justify-between lg:gap-0"
      >
        {/* Account size */}
        <div data-ui="figure.payoutCalculator.account" className="flex w-full flex-col gap-[1rem]">
          <p className={cn(LABEL, 'text-black/64')}>Account size</p>
          <div role="radiogroup" aria-label="Account size" className="flex w-full items-center gap-[0.5rem] rounded-[0.25rem] bg-white p-[0.5rem]">
            {ACCOUNT_SIZES.map((size) => {
              const active = size.value === account;
              return (
                <button
                  key={size.value}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setAccount(size.value)}
                  className={cn(
                    'flex h-[3rem] min-w-0 flex-1 items-center justify-center whitespace-nowrap rounded-[0.25rem] text-[2rem] font-medium leading-[3rem] transition-colors lg:h-[4rem]',
                    active ? 'bg-black text-white' : 'text-black/64 hover:text-black',
                  )}
                >
                  {size.label}
                </button>
              );
            })}
          </div>
        </div>

        <MoneyInput
          id={`${uid}-mdd`}
          label="Max drawdown"
          slug={RULE_LINKS.maxDrawdown}
          value={maxDrawdownRaw}
          onChange={setMaxDrawdownRaw}
          dataUi="figure.payoutCalculator.field-mdd"
        />
        <MoneyInput
          id={`${uid}-cap`}
          label="Payout cap"
          slug={RULE_LINKS.cap}
          value={capRaw}
          onChange={setCapRaw}
          dataUi="figure.payoutCalculator.field-cap"
        />
        <MoneyInput
          id={`${uid}-profit`}
          label="Profit at payout 1"
          slug={RULE_LINKS.profit}
          value={profitRaw}
          onChange={setProfitRaw}
          dataUi="figure.payoutCalculator.field-profit"
        />

        {/* Consistency limit slider */}
        <div data-ui="figure.payoutCalculator.slider" className="flex w-full flex-col gap-[1.5rem]">
          <div className="flex w-full items-start justify-between">
            <label htmlFor={`${uid}-limit`} className="w-fit">
              <RuleLink slug={RULE_LINKS.limit}>Consistency limit</RuleLink>
            </label>
            <span className={cn(LABEL, 'text-black')}>{limit}%</span>
          </div>
          <div className="relative h-[2rem] w-full rounded-[0.25rem]">
            <div className="absolute left-0 top-[0.75rem] h-[0.5rem] w-full rounded-[0.25rem] bg-black/10" />
            <div
              data-ui="figure.payoutCalculator.slider-fill"
              className="absolute left-0 top-[0.75rem] h-[0.5rem] rounded-[0.25rem] bg-[#228e71]"
              style={{ width: `${fill}%` }}
            />
            <img
              alt=""
              src={`${ASSETS}/slider-thumb.svg`}
              data-ui="figure.payoutCalculator.slider-thumb"
              className="pointer-events-none absolute top-0 h-[2rem] w-[2rem] max-w-none"
              style={{ left: `calc(${fill}% - 1rem)` }}
            />
            <input
              id={`${uid}-limit`}
              type="range"
              min={LIMIT_MIN}
              max={LIMIT_MAX}
              step={1}
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              aria-label="Consistency limit"
              aria-valuetext={`${limit}%`}
              className="absolute left-0 top-0 m-0 h-[2rem] w-full cursor-pointer appearance-none bg-transparent opacity-0 [&::-moz-range-thumb]:h-[2rem] [&::-moz-range-thumb]:w-[2rem] [&::-moz-range-thumb]:border-0 [&::-webkit-slider-thumb]:h-[2rem] [&::-webkit-slider-thumb]:w-[2rem] [&::-webkit-slider-thumb]:appearance-none"
            />
          </div>
        </div>

        <MoneyInput
          id={`${uid}-request`}
          label="Payout 2 request"
          slug={RULE_LINKS.request}
          value={requestRaw}
          onChange={setRequestRaw}
          dataUi="figure.payoutCalculator.field-request"
        />
      </div>

      {/* Resultados da calculadora */}
      <div data-ui="figure.payoutCalculator.results" className="flex w-full min-w-0 flex-col gap-[1rem] lg:flex-1">
        <Answer
          dataUi="figure.payoutCalculator.answer-1"
          question={
            <>
              How much can I withdraw
              <br className="lg:hidden" /> at payout 1?
            </>
          }
          note={cappedByCap ? 'limited by the Payout Cap' : 'limited to 50% of your profit'}
          value={money(payout1)}
          tone="green"
        />
        <Answer
          dataUi="figure.payoutCalculator.answer-2"
          question="How big may my best day be?"
          note={`consistency limit ${limit}% of your profit`}
          value={money(bestDay)}
        />
        <Answer
          dataUi="figure.payoutCalculator.answer-3"
          question="What stays in my account?"
          note={
            <>
              your buffer + your drawdown
              <br className="lg:hidden" /> room after the payout
            </>
          }
          value={money(stays)}
        />
        <Answer
          dataUi="figure.payoutCalculator.answer-4"
          question={
            <>
              Where is my Max Drawdown
              <br className="lg:hidden" /> line before the payout?
            </>
          }
          note={locked ? 'already locked at your starting balance' : `trailing — locks once your profit reaches ${money(maxDrawdown)}`}
          value={money(lineBefore)}
        />
        <Answer
          dataUi="figure.payoutCalculator.answer-5"
          question={
            <>
              Where is my Max Drawdown
              <br className="lg:hidden" /> line after the payout?
            </>
          }
          note={locked ? 'stays at your starting balance' : 'moved up to your starting balance'}
          value={money(lineAfter)}
        />
        <Answer
          dataUi="figure.payoutCalculator.answer-6"
          question={
            <>
              How much new profit
              <br className="lg:hidden" /> do I need for payout 2?
            </>
          }
          note={request2 < request ? `to request ${money(request2)} — the Payout Cap` : `to request ${money(request2)}`}
          value={money(newProfit)}
          tone="green"
        />
      </div>
    </FigureFrame>
  );
}
